import { ReactNode, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ConnectedRouter } from 'connected-react-router';
import { Notifications } from '@mantine/notifications';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ColorSchemeProvider, MantineProvider } from '@mantine/core';
import {
	AUTOCLOSE_DELAY, CONTAINER_STYLE, DEFAULT_ZINDEX, NOTIFICATIONS_LIMIT
} from '@ui/systems/SnackbarSystem';

import { ApiProvider } from './ApiProvider';
import RootStoreProvider, { useRootStore } from './RootStoreProvider';
import PersistQueryClientProvider from './PersistQueryClientProvider';
import { persistor, store } from '../../store/store';
import history from '../../navigation/history';
import ReduxTanStackSyncProvider from './ReduxTanStackSyncProvider';
import theme from '../modules/ui/theme';
import '../modules/ui/tokens/colors/semanticColors.scss';

const MantineWrapper = observer( ( { children }: { children: ReactNode } ) => {
	const { colorSchemeStore } = useRootStore();
	const { colorScheme } = colorSchemeStore;

	useEffect( () => {
		document.documentElement.setAttribute( 'data-mantine-color-scheme', colorScheme );
	}, [ colorScheme ] );

	return (
		<ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={colorSchemeStore.toggle}>
			<MantineProvider
				withCSSVariables
				theme={{ ...theme, colorScheme }}
			>
				<Notifications
					autoClose={AUTOCLOSE_DELAY}
					limit={NOTIFICATIONS_LIMIT}
					zIndex={DEFAULT_ZINDEX}
					style={CONTAINER_STYLE}
				/>
				<ApiProvider>
					{children}
				</ApiProvider>
			</MantineProvider>
		</ColorSchemeProvider>
	);
} );

const AppProvider = ( { children }: { children: ReactNode } ) => (
	<RootStoreProvider>
		<ReduxProvider store={store}>
			<PersistGate loading={null} persistor={persistor}>
				<ConnectedRouter history={history}>
					<PersistQueryClientProvider>
						<ReduxTanStackSyncProvider>
							<MantineWrapper>
								{children}
							</MantineWrapper>
						</ReduxTanStackSyncProvider>
						<ReactQueryDevtools initialIsOpen={false} />
					</PersistQueryClientProvider>
				</ConnectedRouter>
			</PersistGate>
		</ReduxProvider>
	</RootStoreProvider>
);

export default AppProvider;
